import { useEffect, useState } from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { useIntl, FormattedMessage } from "react-intl";
import Radio from "components/atoms/Radio/Radio";
import { Row, Col } from "react-bootstrap";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Alert from "components/molecules/Alert/Alert";
import Button from "components/atoms/Button/Button";
import { assignCustomsBroker, getCustomsBrokers } from "api";
import { toast } from "react-toastify";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import propTypes from "prop-types";

import Dialog from "components/organisms/Dialog/Dialog";
import errorToast from "helpers/errorToast";

function CustomsBrokerDialog({
  show,
  close,
  shipmentId,
  setCustomsBrokersCompleted,
  isApprovalRequested,
  setShowBookQuoteDialog,
}) {
  const intl = useIntl();
  const methods = useForm({
    defaultValues: {
      customs_broker_enabled: "",
    },
  });
  const { watch, setValue, getValues } = methods;
  watch();

  const [isLoadingBrokers, setIsLoadingBrokers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customsBrokers, setCustomsBrokers] = useState([]);
  const selectedCustomBroker = customsBrokers.find((item) => item.value === getValues("customs_broker"));

  const customsBrokerEnabled = useWatch({
    control: methods.control,
    name: "customs_broker_enabled",
  });

  const brokerSelected = useWatch({
    control: methods.control,
    name: "customs_broker",
  });

  const handleGetCustomsBrokers = () => {
    if (!shipmentId) return;
    setIsLoadingBrokers(true);
    getCustomsBrokers(shipmentId)
      .then((res) => {
        const allBrokers = res.data.results
          .map((broker) => {
            if (!broker.id || (!broker.display_name && !broker.company_name)) return;
            return {
              label: broker.display_name || broker.company_name,
              value: broker.id,
            };
          })
          .filter(Boolean);
        setCustomsBrokers(allBrokers);
        // If a customs broker has already been selected earlier, it will set it as default value
        const assignedBroker = res.data.results.find((broker) => broker.is_selected);
        if (assignedBroker) {
          setValue("customs_broker_enabled", true);
          setValue("customs_broker", assignedBroker.id);
        }
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsLoadingBrokers(false));
  };

  const handleCustomBroker = () => {
    setIsSubmitting(true);
    const payload = {
      is_invited: getValues("customs_broker_enabled") !== "",
      ...(getValues("customs_broker_enabled") && { customs_broker_id: getValues("customs_broker") }),
    };
    assignCustomsBroker(shipmentId, payload)
      .then((res) => {
        if (res.status === 200) {
          toast(
            intl.formatMessage({
              id: "shipment-quotes.components.customsBrokerDialog.successMessage",
              defaultMessage: "Customs broker team updated.",
            }),
            {
              type: "success",
            },
          );
          close();
          setCustomsBrokersCompleted(true);
        }
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    handleGetCustomsBrokers();
  }, []);

  return (
    <Dialog
      size="md"
      title={intl.formatMessage({
        id: "general.customsBrokers",
        defaultMessage: "Customs brokers",
      })}
      show={show}
      onHide={() => {
        close();
        setShowBookQuoteDialog(false);
      }}
    >
      <FormProvider {...methods}>
        {isLoadingBrokers && <LoadingSkeleton noShadow isListing rows={1} />}
        {!isLoadingBrokers && (
          <form>
            {isApprovalRequested ? (
              <div className="font-b2">
                {/* Selected Custom Broker: <b>{selectedCustomBroker?.label}</b> */}
                Custom Broker Selected? <b>{getValues("customs_broker") ? "Yes" : "No"}</b>{" "}
                {getValues("customs_broker") && <b>({selectedCustomBroker?.label})</b>}
              </div>
            ) : (
              <>
                <Row className="mb-3">
                  <Col>
                    <FormattedMessage
                      id="shipment-quotes.components.customsBrokerDialog.inviteBroker"
                      defaultMessage="Do you want to invite a customs broker for this shipment?"
                    />
                  </Col>
                  <Col className="d-flex">
                    <Radio
                      noBorder
                      eventContext
                      name="customs_broker_enabled"
                      options={[
                        { label: "Yes", value: true },
                        { label: "No", value: "" },
                      ]}
                      customOption
                      className="border-grey"
                      defaultChecked
                    />
                  </Col>
                </Row>
                {customsBrokers.length > 0 && customsBrokerEnabled && !isLoadingBrokers && (
                  <>
                    <Row>
                      <Col>
                        <Dropdown
                          useHookForm
                          name="customs_broker"
                          placeholder="Select customs broker"
                          options={customsBrokers}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Alert className="mt-2 ml-3" variant="info">
                        <FormattedMessage
                          id="shipment-quotes.components.customsBrokerDialog.infoMessage"
                          defaultMessage="The selected customs broker will be invited after the booking has been completed."
                        />
                      </Alert>
                    </Row>
                  </>
                )}
              </>
            )}
            <Row className="mt-3">
              <Col className="d-flex justify-content-between">
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    close();
                    setShowBookQuoteDialog(false);
                  }}
                >
                  Discard
                </Button>
                <Button
                  isLoading={isSubmitting}
                  onClick={handleCustomBroker}
                  disabled={!brokerSelected && customsBrokerEnabled}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </form>
        )}
      </FormProvider>
    </Dialog>
  );
}

CustomsBrokerDialog.propTypes = {
  show: propTypes.bool.isRequired,
  close: propTypes.func.isRequired,
  shipmentId: propTypes.string.isRequired,
  setCustomsBrokersCompleted: propTypes.func.isRequired,
  isApprovalRequested: propTypes.bool.isRequired,
  setShowBookQuoteDialog: propTypes.func.isRequired,
};

export default CustomsBrokerDialog;
